/**
 * x-product-recommendations directive.
 *
 * Lazily fetches and morphs in product recommendations HTML from the Shopify
 * Section Rendering API. The `request()` method must be called explicitly (e.g.
 * via `x-intersect`) and will only fetch once per element lifecycle.
 * Dispatches `product-recommendations:loaded` after the morph completes.
 *
 * @options
 *   url      - The section rendering URL to fetch HTML from.
 *   selector - CSS selector used to extract the target element from the response.
 *
 * @example
 *   <div x-product-recommendations="{ url: '…', selector: '#Recommendations' }"
 *        x-intersect="request()">
 *   </div>
 */
(() => {
  const root = function (Alpine, el, { url, selector }) {
    Alpine.bind(el, () => {
      return {
        ["x-data"]() {
          return {
            loaded: false,

            /**
             * Fetches the recommendations HTML and morphs it into the element.
             * No-ops if `url` is not set or if a request has already been made.
             * Dispatches `product-recommendations:loaded` on success.
             */
            request() {
              if (!url) return;
              if (this.loaded) return;

              this.loaded = true;

              this.$app.$prefetchManager
                .fetch(url)
                .then((response) => response.text())
                .then((text) => {
                  const html = new DOMParser()
                    .parseFromString(text, "text/html")
                    .querySelector(selector);

                  if (html) {
                    Alpine.morph(this.$el, html);
                  }

                  this.$nextTick(() => {
                    this.$dispatch("product-recommendations:loaded");
                  });
                })
                .catch((e) => {
                  console.error(e);
                });
            },
          };
        },
      };
    });
  };

  document.addEventListener("alpine:init", function () {
    Alpine.directive(
      "product-recommendations",
      (el, { expression }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};
        root(Alpine, el, options);
      },
    ).before("bind");
  });
})();
